import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { fromTimestamp } from "@/lib/firestore";
import { relativeDate } from "@/lib/humanDate";
import { formatActorName } from "@/lib/notificationActor";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import type { UserNotification } from "@/lib/models";
import { routeForNotification } from "@/lib/notificationRoutes";

// Maps every iOS `view_model_type` from `NotificationCategoryInfo.from()`
// (`zoey_ios/.../Views/NotificationView.swift:12-44`) to a web emoji + tint.
// iOS uses SF Symbols; web uses emoji per the parity sweep's intentional
// web-leads (more accessible/legible). Tint colors mirror the iOS palette.
const CATEGORY_CONFIG: Record<string, { emoji: string; color: string }> = {
  // iOS-canonical view_model_type values (12 categories)
  chore_assigned: { emoji: "📋", color: "#FF9500" },
  post_created: { emoji: "📝", color: "#007AFF" },
  post_reply: { emoji: "↩️", color: "#007AFF" },
  chat_created: { emoji: "💬", color: "#34C759" },
  chat_reply: { emoji: "↩️", color: "#34C759" },
  event_created: { emoji: "📅", color: "#AF52DE" },
  responsibility_claimed: { emoji: "✋", color: "#5AC8C8" },
  responsibility_completed: { emoji: "✅", color: "#5AC8C8" },
  responsibility_reschedule: { emoji: "🔄", color: "#5AC8C8" },
  responsibility_reminder: { emoji: "⏰", color: "#5AC8C8" },
  member_joined: { emoji: "👋", color: "#5856D6" },
  area_nudge: { emoji: "📍", color: "#FF9500" },
  // Aliases for older / generic notification.category values
  post: { emoji: "📝", color: "#007AFF" },
  chat: { emoji: "💬", color: "#34C759" },
  message: { emoji: "💬", color: "#34C759" },
  chore: { emoji: "✅", color: "#34C759" },
  shopping: { emoji: "🛒", color: "#FF9500" },
  grocery: { emoji: "🥕", color: "#FF9500" },
  responsibility: { emoji: "📋", color: "#5AC8C8" },
  calendar: { emoji: "📅", color: "#AF52DE" },
  event: { emoji: "📅", color: "#AF52DE" },
  household: { emoji: "🏠", color: "#00C7BE" },
};

function getCategoryConfig(notification: UserNotification) {
  // Prefer specific view_model_type (chore_assigned, post_reply, etc.)
  // over generic category since iOS distinguishes the subtypes for icon
  // selection. Fall back to category, then to a generic bell.
  const vmt = notification.view_model_type?.toLowerCase();
  if (vmt && CATEGORY_CONFIG[vmt]) return CATEGORY_CONFIG[vmt];
  const cat = notification.category?.toLowerCase();
  if (cat && CATEGORY_CONFIG[cat]) return CATEGORY_CONFIG[cat];
  return { emoji: "🔔", color: "#8E8E93" };
}

function groupByDate(
  notifications: UserNotification[],
  t: (key: string) => string,
) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups: { label: string; items: UserNotification[] }[] = [
    { label: t("notifications.group.today"), items: [] },
    { label: t("notifications.group.yesterday"), items: [] },
    { label: t("notifications.group.thisWeek"), items: [] },
    { label: t("notifications.group.earlier"), items: [] },
  ];

  for (const n of notifications) {
    const date = fromTimestamp(n.created_at);
    if (!date) {
      groups[3].items.push(n);
      continue;
    }

    if (date >= today) {
      groups[0].items.push(n);
    } else if (date >= yesterday) {
      groups[1].items.push(n);
    } else if (date >= weekAgo) {
      groups[2].items.push(n);
    } else {
      groups[3].items.push(n);
    }
  }

  return groups.filter((g) => g.items.length > 0);
}

export function NotificationsPage() {
  const { profile, loading: contextLoading } = useHouseholdContext();
  const navigate = useNavigate();
  const t = useTranslations();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.uid) {
      setLoading(false);
      return;
    }

    const ref = collection(db, "user_notifications");
    const q = query(
      ref,
      where("user_id", "==", profile.uid),
      orderBy("created_at", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map(
          (d) => ({ ...d.data(), uid: d.id }) as UserNotification
        );
        setNotifications(items);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching notifications:", error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [profile?.uid]);

  const markAsRead = async (notificationId: string) => {
    try {
      const ref = doc(db, "user_notifications", notificationId);
      await updateDoc(ref, { is_read: true });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter((n) => n.is_read !== true);
    if (unread.length === 0) return;

    try {
      const batch = writeBatch(db);
      for (const n of unread) {
        if (!n.uid) continue;
        const ref = doc(db, "user_notifications", n.uid);
        batch.update(ref, { is_read: true });
      }
      await batch.commit();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = (notification: UserNotification) => {
    if (notification.uid && notification.is_read !== true) {
      markAsRead(notification.uid);
    }
    const route = routeForNotification(notification);
    navigate(route);
  };

  if (loading || contextLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  const unreadCount = notifications.filter((n) => n.is_read !== true).length;
  const groups = groupByDate(notifications, t);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("notifications.title")}</h1>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-sm font-medium"
            style={{ color: "var(--accent-color, #00C7BE)" }}
          >
            {t("notifications.markAllRead")}
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <EmptyState
          icon="🔕"
          title={t("notifications.empty.title")}
          subtitle={t("notifications.empty.subtitle")}
        />
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.label} className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
                {group.label}
              </p>
              <Card padding="p-0">
                <div className="divide-y divide-gray-100 dark:divide-white/5">
                  {group.items.map((notification) => {
                    const config = getCategoryConfig(notification);
                    const date = fromTimestamp(notification.created_at);
                    const isUnread = notification.is_read !== true;

                    return (
                      <button
                        key={notification.uid}
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full flex items-start gap-3 p-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/5 ${
                          isUnread ? "bg-blue-50/30 dark:bg-blue-900/10" : ""
                        }`}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-sm shrink-0"
                          style={{
                            backgroundColor: config.color + "18",
                          }}
                        >
                          {config.emoji}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm leading-snug ${
                              isUnread ? "font-semibold" : "text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {(() => {
                              const actor = formatActorName(notification.actor_name);
                              return actor ? (
                                <span className="font-bold">{actor}{" "}</span>
                              ) : null;
                            })()}
                            {notification.view_model_text}
                          </p>
                          {notification.detail && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate">
                              {notification.detail}
                            </p>
                          )}
                          {date && (
                            <p className="text-xs text-gray-400 mt-1">
                              {relativeDate(date)}
                            </p>
                          )}
                        </div>
                        {isUnread && (
                          <div
                            className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5"
                            style={{
                              backgroundColor: "var(--accent-color, #00C7BE)",
                            }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
